/**
 * Hybrid Socket Manager
 * Automatically switches between real Socket.IO (online) and offline fallback
 */

import offlineSocket from './offlineSocket';

class HybridSocket {
  constructor() {
    this.realSocket = null; // Real Socket.IO instance
    this.isBackendOnline = true;
    this.eventHandlers = new Map(); // event -> Set of callbacks (for re-registration)
    
    console.log('✅ HybridSocket: Initialized');
  }

  /**
   * Set the real Socket.IO instance
   */
  setRealSocket(socket) {
    this.realSocket = socket;
    console.log('🔌 HybridSocket: Real socket connected');
    
    // Re-register all event handlers on real socket
    this.eventHandlers.forEach((callbacks, event) => {
      if (this.realSocket) {
        callbacks.forEach(callback => this.realSocket.on(event, callback));
      }
    });
  }

  /**
   * Mark backend as online/offline
   */
  setBackendStatus(isOnline) {
    const wasOnline = this.isBackendOnline;
    this.isBackendOnline = isOnline;
    
    if (wasOnline !== isOnline) {
      console.log(`🔄 HybridSocket: Backend ${isOnline ? 'ONLINE' : 'OFFLINE'} - switching to ${isOnline ? 'real' : 'offline'} socket`);
      // Update global backend health flag so axios retries are skipped when backend is down
      window._backendHealthy = isOnline;
    }
    
    offlineSocket.setOnlineStatus(isOnline);
  }

  /**
   * Register event listener (works for both online and offline)
   */
  on(event, callback) {
    // Store handler for re-registration (support multiple per event)
    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, new Set());
    }
    this.eventHandlers.get(event).add(callback);
    
    // Register on real socket if available
    if (this.realSocket && this.isBackendOnline) {
      this.realSocket.on(event, callback);
      console.log(`📡 HybridSocket: Registered "${event}" on real socket`);
    }
    
    // Always register on offline socket as fallback
    offlineSocket.on(event, callback);
  }

  /**
   * Remove event listener
   */
  off(event, callback) {
    // Remove specific callback from the set (don't remove other listeners)
    if (this.eventHandlers.has(event)) {
      this.eventHandlers.get(event).delete(callback);
      if (this.eventHandlers.get(event).size === 0) {
        this.eventHandlers.delete(event);
      }
    }
    
    if (this.realSocket) {
      this.realSocket.off(event, callback);
    }
    
    offlineSocket.off(event, callback);
  }

  /**
   * Emit event (automatically chooses online or offline)
   */
  emit(event, ...args) {
    if (this.realSocket && this.isBackendOnline) {
      // Use real Socket.IO
      this.realSocket.emit(event, ...args);
      console.log(`📤 HybridSocket: Emitted "${event}" via real socket`, args);
    } else {
      // Use offline fallback
      offlineSocket.emit(event, args.length === 1 ? args[0] : args);
      console.log(`📤 HybridSocket: Emitted "${event}" via offline socket`, args);
    }
  }

  /**
   * Disconnect
   */
  disconnect() {
    if (this.realSocket) {
      this.realSocket.disconnect();
    }
    offlineSocket.disconnect();
    this.eventHandlers.clear();
    console.log('🔌 HybridSocket: Disconnected');
  }

  /**
   * Get connection status
   */
  isConnected() {
    if (this.isBackendOnline && this.realSocket) {
      return this.realSocket.connected;
    }
    // Offline socket is always "connected" (uses BroadcastChannel/localStorage)
    return true;
  }
}

// Export singleton instance
const hybridSocket = new HybridSocket();
export default hybridSocket;
